import { AppNS } from 'app';
import { getHourFormatted, UI_DATE_FORMAT_STRING } from 'app/helpers/parseDate';
import PermitsInfo from 'app/main/common/bookings/permits-info/PermitsInfo';
import { ExternalButton } from 'app/ui/components/external-button/ExternalButton';
import { parseDate } from 'app/ui_v2/helpers/parseDate';
import { unescape } from 'lodash-es';
import { Link } from 'react-router-dom';

import { Box, Card, CardContent, Divider, SxProps, Theme, useTheme } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { getInspectionExternalLink, getProjectExternalLink } from '../../config';
import Geolocation from '../geolocation/Geolocation';
import Inspector from '../inspector/Inspector';
import ReportId from '../report-id/ReportId';
import SubmissionTime from '../submission-time/SubmissionTime';
import UnsafeConditions from '../unsafe-conditions/UnsafeConditions';

export interface InspectionsListProps {
  data: Array<AppNS.JobEventReport>;
}

const getStyles = (theme: Theme): { [key: string]: SxProps } => {
  return {
    table: {
      width: '100%',
      margin: '16px 0',
    },
    dateCell: {
      fontWeight: 'bold',
    },
    view: {
      background: theme.palette.primary.main,
      padding: '4px',
      height: '26px',
      width: '26px',
      borderRadius: '4px',
      marginRight: '24px',
      color: '#fff',
    },
    titleCase: {
      textTransform: 'capitalize',
    },
  };
};

export default function InspectionsList(props: InspectionsListProps) {
  const { data } = props;
  const theme = useTheme();
  const styles = getStyles(theme);
  return (
    <Card>
      <CardContent>
        {data.map((item: AppNS.JobEventReport, index: number) => (
          <Box key={item.id}>
            {index > 0 && <Divider />}

            <Table sx={styles.table} className={'no-borders'} aria-label="Job lead table">
              <TableHead>
                <TableRow>
                  <TableCell>Event #</TableCell>
                  <TableCell>
                    <ReportId id={item.id} />
                    <UnsafeConditions data={item} />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>
                    {parseDate(item.date, UI_DATE_FORMAT_STRING)}
                    <br />
                    {getHourFormatted(item.timeFrom)} - {getHourFormatted(item.timeTo)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Project # / Address</TableCell>
                  <TableCell>
                    <ExternalButton style={{ textDecoration: 'none' }} to={getProjectExternalLink(item.bookingId)}>
                      Project #{item.jobId}
                    </ExternalButton>
                    <Box sx={styles.titleCase}>{item.address}</Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Inspector</TableCell>
                  <TableCell>
                    <Inspector item={item} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>PIC/MCP</TableCell>
                  <TableCell>{item.picInspector && <>{unescape(item.picInspector.fullName)}</>}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Agency</TableCell>
                  <TableCell>{item.agencyCompany && <>{unescape(item.agencyCompany.name)}</>}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>{item.client && <>{unescape(item.client.fullName)}</>}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Permit #</TableCell>
                  <TableCell scope="row">
                    {item.permitNumbers && <PermitsInfo permits={item.permitNumbers} />}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Geolocation</TableCell>
                  <TableCell scope="row">
                    <Geolocation item={item} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Report Submission Time</TableCell>
                  <TableCell scope="row">
                    <SubmissionTime data={item} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <ExternalButton
                      variant="contained"
                      color="primary"
                      component={Link}
                      to={getInspectionExternalLink(item.id)}
                    >
                      View
                    </ExternalButton>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}
